import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Switch, Route } from 'react-router-dom';
import useDetectSize from '@hooks/useDetectSize';
import { Page, MobileContainer, DesktopContainer } from '@containers/layout';
import { IconButton } from '@components/common';
import DialogBox from '@components/DialogBox';
import FullScreenLoader from '@components/loading/FullScreenLoader';
import CancelConfirm from '@containers/booking/Confirm/CancelConfirm';

import {
  SubscriptionEdit,
  SubscriptionDetail,
  SubscriptionEmpty,
  CycleCalendarView
} from '@containers/subscription';
import history from '@utils/history';

import SubscriptionActions, {
  SubscriptionSelectors
} from '@redux/SubscriptionRedux';
import DiscontinueActions, {
  DISCONTINUE_STAGE,
  DISCONTINUE_TYPE
} from '@redux/DiscontinueRedux';
import { AuthSelectors } from '@redux/AuthRedux';

const ContainerView = styled(MobileContainer)`
  align-self: flex-start;
  align-items: stretch;

  @media screen and (max-width: 767px) {
    justify-content: flex-start;
  }

  @media screen and (min-width: 1024px) {
    padding-top: 0px;
  }
`;

function SubscriptionPage(props) {
  const {
    match: { url },
    loadSubscriptions,
    activeSubscription,
    loading,
    setDiscontinueType,
    setDiscontinueStep,
    setDiscontinueInitialStep,
    hasActivePropertySetting
  } = props;

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const isMobile = useDetectSize();
  const [show, toggleModal] = useState(false);

  const goBack = () => {
    history.goBack();
  };

  const renderBackButton = () => {
    return (
      <IconButton
        onClick={goBack}
        name="back"
        width={16}
        height={16}
        title="Back"
      />
    );
  };

  const handleOnModify = () => {
    history.push(`${url}/edit`);
  };

  const handleOnPause = () => {
    handleConfirmDiscontinue(true);
  };

  const handleOnStop = () => {
    if (hasActivePropertySetting) {
      handleConfirmDiscontinue();
      return;
    }

    toggleModal(true);
  };

  const handleConfirmDiscontinue = isPausing => {
    toggleModal(false);
    if (isPausing) {
      pauseService();
      return;
    }

    stopService();
  };

  const handleDissmisModal = () => {
    toggleModal(false);
  };

  const pauseService = () => {
    setDiscontinueStep(DISCONTINUE_STAGE.DATE);
    setDiscontinueInitialStep(DISCONTINUE_STAGE.DATE);
    setDiscontinueType(DISCONTINUE_TYPE.PAUSE);
    history.push('/discontinue');
  };

  const stopService = () => {
    setDiscontinueStep(DISCONTINUE_STAGE.TYPE);
    history.push('/discontinue');
  };

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!activeSubscription) {
    return (
      <Page title="Routine" renderHeaderLeftButtons={renderBackButton}>
        <SubscriptionEmpty
          onSubmit={() => {
            history.push('/book');
          }}
        />
      </Page>
    );
  }

  return (
    <Switch>
      <Route exact path={`${url}`}>
        <Page title="Routine" renderHeaderLeftButtons={renderBackButton}>
          <DesktopContainer>
            <ContainerView>
              <SubscriptionDetail
                onModify={handleOnModify}
                onPause={handleOnPause}
                onStop={handleOnStop}
              />
            </ContainerView>
            {!isMobile && (
              <CycleCalendarView
                flex={1}
                onModify={handleOnModify}
                onPause={handleOnPause}
                onStop={handleOnStop}
              />
            )}
            <DialogBox show={show} onClickOutside={handleDissmisModal}>
              <CancelConfirm
                cancelLabel="Keep Service"
                submitLabel="Cancel Service"
                title="Cancel Service"
                description="You are currently locked into our 2019 pricing. If you discontinue, you will no longer be able to take advantage of those reduced rates. Would you like to continue?"
                onSubmit={() => handleConfirmDiscontinue(false)}
                onCancel={handleDissmisModal}
              />
            </DialogBox>
          </DesktopContainer>
        </Page>
      </Route>
      <Route exact path={`${url}/edit`} component={SubscriptionEdit} />
    </Switch>
  );
}

SubscriptionPage.propTypes = {
  loading: PropTypes.bool,
  loadSubscriptions: PropTypes.func,
  activeSubscription: PropTypes.object,
  match: PropTypes.object,
  setDiscontinueType: PropTypes.func,
  setDiscontinueStep: PropTypes.func,
  setDiscontinueInitialStep: PropTypes.func,
  hasActivePropertySetting: PropTypes.bool,
  isPaused: PropTypes.bool
};

const mapStatesToProps = state => ({
  loading: SubscriptionSelectors.selectLoading(state),
  activeSubscription: SubscriptionSelectors.selectActiveSubscription(state),
  hasActivePropertySetting: AuthSelectors.selectHasActivePropertySetting(state),
  isPaused: SubscriptionSelectors.selectIsPaused(state)
});

const mapDispatchToProps = dispatch => ({
  loadSubscriptions: () =>
    dispatch(SubscriptionActions.setSubscriptionLoadRequest()),
  setDiscontinueType: type =>
    dispatch(DiscontinueActions.setDiscontinueType(type)),
  setDiscontinueStep: step =>
    dispatch(DiscontinueActions.setDiscontinueStep(step)),
  setDiscontinueInitialStep: step =>
    dispatch(DiscontinueActions.setDiscontinueInitialStep(step))
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(SubscriptionPage);
